'use client'

import { hubCardSearchFragment } from '@vendure/hub/data-access'
import { formatCurrency } from '@vendure/hub/util'
import { Badge } from '@vendure/shadcn/ui'
import { CustomLink, GradientCard, Heading } from '@vendure/shared/ui'
import { ResultOf } from 'gql.tada'
import Image from 'next/image'
import type React from 'react'
import { HiDownload } from 'react-icons/hi'

interface IPluginCard {
  product: ResultOf<typeof hubCardSearchFragment>
}

export const HubCard: React.FC<IPluginCard> = ({ product: prod }) => {
  const integrationType = prod.customMappings.integrationType
  const imageUrl = `${prod.productAsset?.preview}?w=50&h=50&fpy=${prod.productAsset?.focalPoint?.y}&fpx=${prod.productAsset?.focalPoint?.x}&mode=crop&format=webp`

  return (
    <GradientCard
      as={CustomLink}
      href={'/hub/' + prod.slug}
      containerProps={{
        className:
          'flex flex-col justify-between  p-8 min-h-48 transition-colors duration-500',
      }}
    >
      <div className="flex flex-grow gap-4">
        <Image
          className="h-[50px] w-[50px] rounded-md object-cover"
          width={150}
          height={150}
          src={imageUrl}
          alt={`${prod.productName} icon`}
        />
        <div className="flex-grow">
          <Heading className="text-xl">{prod.productName}</Heading>
          {prod.customMappings.publisherName && (
            <div className="text-sm text-white/50">
              by {prod.customMappings.publisherName}
            </div>
          )}
          <div
            className="mt-2 text-sm"
            dangerouslySetInnerHTML={{ __html: prod.description }}
          />
        </div>
      </div>
      {integrationType && (
        <div className="mt-4 flex space-x-2">
          <Badge
            style={{ backgroundColor: integrationType.color ?? '' }}
            variant="secondary"
          >
            {integrationType.name}
          </Badge>
          {prod.customMappings.monthlyPrice ? (
            <Badge variant="outline">
              {prod.customMappings.monthlyPrice && (
                <span className="mr-1">
                  from{' '}
                  {formatCurrency(
                    prod.customMappings.monthlyPrice,
                    prod.currencyCode,
                  )}
                </span>
              )}
            </Badge>
          ) : (
            <Badge variant="outline">Free</Badge>
          )}
          {prod.customMappings?.downloadCount ? (
            <Badge
              variant="outline"
              className="flex items-center space-x-1"
            >
              <HiDownload /> {prod.customMappings.downloadCount}
            </Badge>
          ) : null}
        </div>
      )}
    </GradientCard>
  )
}
